import React, { useCallback, useEffect, useRef } from 'react';
import { ArrowsUpDownIcon } from '@heroicons/react/24/outline';
import type { KpiDrilldownSpec, KpiHeatmapCiApiData } from './kpiDashboardApi';
import KpiChartPanel from './charts/KpiChartPanel';
import { heatCellStyleIncidentBlue } from './KpiCustomerHeatmapTable';
import {
  KPI_DASHBOARD_TABLE_ROW_SELECTED,
  KPI_DASHBOARD_TABLE_TH_STICKY_FIRST_COL,
  KPI_DASHBOARD_TABLE_TH_STICKY_TOP,
  kpiDashboardTableRowClass,
} from './kpiDashboardTableStyles';

export type CiHeatmapRow = KpiHeatmapCiApiData['heatmap']['rows'][number];

/** Table selection uses ci* keys; API/query uses resourceId/label/weekStart. */
export type KpiCiTableFocus = {
  ciResourceId: string | null;
  ciLabel: string | null;
  weekStart: string | null;
};

function rowMatchesFocus(row: CiHeatmapRow, focus: KpiCiTableFocus | null): boolean {
  if (!focus) return false;
  if (focus.ciResourceId && row.ciResourceId) return focus.ciResourceId === row.ciResourceId;
  if (focus.ciLabel != null && focus.ciLabel.trim() !== '') {
    return focus.ciLabel.trim().toLowerCase() === row.ciLabel.trim().toLowerCase();
  }
  return false;
}

export interface KpiCiHeatmapTableProps {
  title: string;
  heatmap: KpiHeatmapCiApiData['heatmap'];
  sortedRows: CiHeatmapRow[];
  onToggleCiSort: () => void;
  maxCount: number;
  focus: KpiCiTableFocus | null;
  onToggleCi?: (row: CiHeatmapRow) => void;
  onToggleWeek?: (weekStart: string) => void;
  onToggleCell?: (row: CiHeatmapRow, weekStart: string) => void;
  onCellDrill?: (spec: KpiDrilldownSpec) => void;
  emptyMessage?: string;
}

const KpiCiHeatmapTable: React.FC<KpiCiHeatmapTableProps> = ({
  title,
  heatmap,
  sortedRows,
  onToggleCiSort,
  maxCount,
  focus,
  onToggleCi,
  onToggleWeek,
  onToggleCell,
  onCellDrill,
  emptyMessage = 'No Incident or Request volume with a configuration item in this period for the selected filters.',
}) => {
  const cellClickTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const CELL_CLICK_DEBOUNCE_MS = 280;

  useEffect(
    () => () => {
      if (cellClickTimerRef.current) clearTimeout(cellClickTimerRef.current);
    },
    []
  );

  const scheduleCellToggle = useCallback(
    (row: CiHeatmapRow, weekStart: string) => {
      if (onCellDrill) {
        if (cellClickTimerRef.current) clearTimeout(cellClickTimerRef.current);
        cellClickTimerRef.current = setTimeout(() => {
          cellClickTimerRef.current = null;
          onToggleCell?.(row, weekStart);
        }, CELL_CLICK_DEBOUNCE_MS);
      } else {
        onToggleCell?.(row, weekStart);
      }
    },
    [onCellDrill, onToggleCell]
  );

  const handleCellDoubleClick = useCallback(
    (row: CiHeatmapRow, weekStart: string | undefined) => {
      if (!onCellDrill || !weekStart) return;
      if (cellClickTimerRef.current) {
        clearTimeout(cellClickTimerRef.current);
        cellClickTimerRef.current = null;
      }
      onCellDrill({
        kind: 'heatmap_ci_cell',
        ciResourceId: row.ciResourceId,
        ciLabel: row.ciLabel,
        weekStart,
      });
    },
    [onCellDrill]
  );

  const maxH = maxCount > 0 ? maxCount : 1;
  const stickyNameClass = `${KPI_DASHBOARD_TABLE_TH_STICKY_FIRST_COL} min-w-[12rem] max-w-[16rem]`;

  return (
    <KpiChartPanel
      title={title}
      flexFill
      className="min-h-0"
      bodyClassName="flex min-h-0 flex-1 flex-col px-0 pb-0 pt-0"
    >
      <div className="max-h-[min(28rem,70vh)] min-h-0 overflow-auto">
        <table className="w-max min-w-full border-collapse text-left text-xs">
          <thead>
            <tr>
              <th className={stickyNameClass} scope="col">
                <button
                  type="button"
                  onClick={onToggleCiSort}
                  className="inline-flex items-center gap-1.5 rounded-md px-1 py-0.5 text-left font-semibold text-slate-800 hover:bg-sky-200/50"
                >
                  Configuration Item
                  <ArrowsUpDownIcon className="h-3.5 w-3.5 text-slate-600" aria-hidden />
                </button>
              </th>
              {heatmap.weekLabels.map((wl, i) => {
                const ws = heatmap.weekStarts[i];
                const weekSelected = Boolean(focus?.weekStart && ws === focus.weekStart);
                return (
                  <th key={`${ws ?? i}-${wl}`} className={KPI_DASHBOARD_TABLE_TH_STICKY_TOP} scope="col">
                    <button
                      type="button"
                      className={`w-full rounded-sm px-1 py-0.5 font-semibold text-slate-800 hover:bg-sky-200/50 ${
                        weekSelected ? 'bg-sky-300/40 ring-2 ring-inset ring-sky-600/45' : ''
                      }`}
                      onClick={() => ws && onToggleWeek?.(ws)}
                    >
                      {wl}
                    </button>
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            {sortedRows.length === 0 ? (
              <tr>
                <td
                  colSpan={Math.max(1, heatmap.weekLabels.length + 1)}
                  className="border border-slate-200 bg-white px-4 py-8 text-center text-sm text-slate-500"
                >
                  {emptyMessage}
                </td>
              </tr>
            ) : (
              sortedRows.map((row, ri) => {
                const rowKey = row.ciResourceId ?? row.ciLabel;
                const ciFocused = rowMatchesFocus(row, focus);
                return (
                  <tr key={rowKey} className={`border-b border-slate-200 ${kpiDashboardTableRowClass(ri)}`}>
                    <th
                      scope="row"
                      className={`sticky left-0 z-10 min-w-[12rem] max-w-[16rem] border border-slate-200 px-3 py-2 text-left font-medium shadow-[2px_0_4px_-2px_rgba(15,23,42,0.08)] ${
                        ciFocused ? KPI_DASHBOARD_TABLE_ROW_SELECTED : 'bg-inherit text-slate-900'
                      }`}
                    >
                      <button
                        type="button"
                        className="line-clamp-2 w-full text-left hover:underline"
                        onClick={() => onToggleCi?.(row)}
                      >
                        {row.ciLabel}
                      </button>
                    </th>
                    {row.counts.map((c, i) => {
                      const weekStart = heatmap.weekStarts[i];
                      const clickable = c > 0 && weekStart;
                      const cellSelected =
                        ciFocused && focus?.weekStart != null && weekStart === focus.weekStart;
                      return (
                        <td
                          key={`${rowKey}-${weekStart ?? i}`}
                          className={`relative border border-slate-200 px-1 py-1 text-center tabular-nums text-slate-800 ${
                            clickable ? 'cursor-pointer hover:ring-2 hover:ring-inset hover:ring-[#4472C4]/50' : ''
                          } ${cellSelected ? 'z-[1] ring-2 ring-inset ring-sky-600/50' : ''}`}
                          style={heatCellStyleIncidentBlue(c, maxH)}
                          onClick={
                            clickable
                              ? () => {
                                  if (weekStart) scheduleCellToggle(row, weekStart);
                                }
                              : undefined
                          }
                          onDoubleClick={
                            clickable && onCellDrill
                              ? () => handleCellDoubleClick(row, weekStart)
                              : undefined
                          }
                          onKeyDown={
                            clickable
                              ? (e) => {
                                  if (e.key === 'Enter' || e.key === ' ') {
                                    e.preventDefault();
                                    if (weekStart) scheduleCellToggle(row, weekStart);
                                  }
                                }
                              : undefined
                          }
                          role={clickable ? 'button' : undefined}
                          tabIndex={clickable ? 0 : undefined}
                          aria-label={
                            clickable
                              ? `${row.ciLabel}, ${heatmap.weekLabels[i] ?? 'week'}, count ${c}. Double-click to open tickets.`
                              : undefined
                          }
                        >
                          <span className="inline-block min-w-[1.5rem]">{c > 0 ? c : ''}</span>
                        </td>
                      );
                    })}
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>
    </KpiChartPanel>
  );
};

export default KpiCiHeatmapTable;
